import React from "react";
import { toast } from "react-toastify";
import DateTimePicker from "./DateTimePicker";

// Define props with a more generic setFormData type
type EventSettingsProps = {
	formData: {
		publicationDateTime: string;
		saleStartDateTime: string;
		saleEndDateTime: string;
	};
	setFormData: (value: React.SetStateAction<any>) => void;
	eventDate?: string;
};

const EventSettings: React.FC<EventSettingsProps> = ({
	formData,
	setFormData,
	eventDate,
}) => {
	const isAfterEventDate = (value: string) => {
		if (!eventDate) return false;
		const eventDateObj = new Date(eventDate);
		const saleEndObj = new Date(value);
		if (Number.isNaN(eventDateObj.getTime()) || Number.isNaN(saleEndObj.getTime())) {
			return false;
		}
		const eventEndOfDay = new Date(eventDateObj);
		eventEndOfDay.setHours(23, 59, 59, 999);
		return saleEndObj.getTime() > eventEndOfDay.getTime();
	};

	const formatEventDateLabel = () => {
		if (!eventDate) return "";
		const eventDateObj = new Date(eventDate);
		if (Number.isNaN(eventDateObj.getTime())) return "";
		return `${eventDateObj.getFullYear()}年${eventDateObj.getMonth() + 1}月${eventDateObj.getDate()}日`;
	};

	// Create handler functions that properly update the parent's state
	const handlePublicationDateChange = (value: string) => {
		setFormData((prev: any) => ({
			...prev,
			publicationDateTime: value,
		}));
	};

	const handleSaleStartDateChange = (value: string) => {
		setFormData((prev: any) => ({
			...prev,
			saleStartDateTime: value,
		}));
	};

	const handleSaleEndDateChange = (value: string) => {
		if (value && isAfterEventDate(value)) {
			toast.error(
				`販売終了日時はイベント開催日（${formatEventDateLabel()}）を超えて設定できません`
			);
			return;
		}
		setFormData((prev: any) => ({
			...prev,
			saleEndDateTime: value,
		}));
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<h3 className="text-lg mb-4 font-semibold text-gray-900 dark:text-white pb-2">
				イベントの公開・販売設定
			</h3>
			<div className="space-y-4">
				<div>
					<DateTimePicker
						id="publication-date-time"
						label="公開日時（任意）"
						value={formData.publicationDateTime}
						onChange={handlePublicationDateChange}
						placeholder="公開日時を選択"
					/>
					<p className="text-xs text-gray-500 mt-1">
						設定しない場合は公開ボタンを押した時点で公開されます
					</p>
				</div>

				<div>
					<DateTimePicker
						id="sale-start-date-time"
						label="販売開始日時（任意）"
						value={formData.saleStartDateTime}
						onChange={handleSaleStartDateChange}
						placeholder="販売開始日時を選択"
					/>
					<p className="text-xs text-gray-500 mt-1">
						設定しない場合は公開と同時に販売開始されます
					</p>
				</div>

				<div>
				<DateTimePicker
					id="sale-end-date-time"
					label="販売終了日時（任意）"
					value={formData.saleEndDateTime}
					onChange={handleSaleEndDateChange}
					placeholder="販売終了日時を選択"
				/>
				<p className="text-xs text-gray-500 mt-1">
					設定しない場合はイベント開催日時まで販売されます
					{eventDate && (
						<span className="block text-[11px] text-gray-400">
							※ {formatEventDateLabel()} まで設定可能
						</span>
					)}
				</p>
			</div>
		</div>
		</div>
	);
};

export default EventSettings;
